import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import OnboardingTour from '../onboarding/OnboardingTour'
import ChantierChat from '../chat/ChantierChat'

// ─── NOTE SUPABASE ────────────────────────────────────────────────────────────
// Table chantier_membres necessaire :
// CREATE TABLE IF NOT EXISTS chantier_membres (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   chantier_id uuid REFERENCES chantiers(id) ON DELETE CASCADE,
//   user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE,
//   role text CHECK (role IN ('entreprise', 'client')),
//   rejoint_le timestamptz DEFAULT now(),
//   UNIQUE(chantier_id, user_id)
// );
// ALTER TABLE chantier_membres ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "membres can see their own" ON chantier_membres FOR ALL USING (auth.uid() = user_id);
// ─────────────────────────────────────────────────────────────────────────────

const TACHE_STATUT_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Termine', valide: 'Valide', reserve: 'Reserve' }
const TACHE_STATUT_BADGE = { en_attente: 'badge-gray', en_cours: 'badge-blue', termine: 'badge-orange', valide: 'badge-green', reserve: 'badge-orange' }

// ─── Section Rejoindre un chantier ────────────────────────────────────────────

function RejoindreChantier({ userId, onRejoint }) {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [succes, setSucces] = useState('')
  const [error, setError] = useState('')

  const handleRejoindre = async (e) => {
    e.preventDefault()
    const codeNormalise = code.trim().toUpperCase()
    if (codeNormalise.length !== 6) {
      setError('Le code doit contenir exactement 6 caracteres.')
      return
    }
    setLoading(true)
    setError('')
    setSucces('')

    const { data: chantier, error: chantierError } = await supabase
      .from('chantiers')
      .select('id, nom')
      .eq('code', codeNormalise)
      .single()

    if (chantierError || !chantier) {
      setError('Code invalide. Verifiez le code fourni par votre architecte.')
      setLoading(false)
      return
    }

    const { data: dejaMembre } = await supabase
      .from('chantier_membres')
      .select('id')
      .eq('chantier_id', chantier.id)
      .eq('user_id', userId)
      .single()

    if (dejaMembre) {
      setError('Vous etes deja membre de ce chantier : ' + chantier.nom)
      setLoading(false)
      return
    }

    const { error: insertError } = await supabase.from('chantier_membres').insert([{
      chantier_id: chantier.id,
      user_id: userId,
      role: 'entreprise',
    }])

    if (insertError) {
      setError('Erreur : ' + insertError.message)
      setLoading(false)
      return
    }

    setSucces('Chantier "' + chantier.nom + '" rejoint avec succes !')
    setCode('')
    setLoading(false)
    onRejoint()
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header">
        <span className="card-title">Rejoindre un chantier</span>
      </div>
      <div className="card-body">
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
          Entrez le code a 6 caracteres fourni par votre architecte pour acceder au chantier.
        </p>
        {error && (
          <div style={{ background: '#fff5f5', color: '#e53e3e', fontSize: 13, padding: '10px 12px', borderRadius: 6, borderLeft: '3px solid #e53e3e', marginBottom: 12 }}>
            {error}
          </div>
        )}
        {succes && (
          <div style={{ background: '#e8f7f2', color: '#1D9E75', fontSize: 13, padding: '10px 12px', borderRadius: 6, borderLeft: '3px solid #1D9E75', marginBottom: 12, fontWeight: 500 }}>
            {succes}
          </div>
        )}
        <form onSubmit={handleRejoindre} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--gray-600)', marginBottom: 5 }}>
              Code du chantier
            </label>
            <input
              className="form-input"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: XK4T9P"
              maxLength={6}
              style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase' }}
            />
          </div>
          <button type="submit" className="btn btn-green" disabled={loading || code.length === 0} style={{ height: 42, padding: '0 20px' }}>
            {loading ? 'Recherche...' : 'Rejoindre'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ─── Modal Upload Photo ───────────────────────────────────────────────────────

function ModalUploadPhoto({ tache, onClose, onUploaded }) {
  const [files, setFiles] = useState([])
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (files.length === 0) return
    setLoading(true)
    for (const file of files) {
      const path = 'photos/' + Date.now() + '_' + file.name
      const { error: uploadError } = await supabase.storage.from('chantier-photos').upload(path, file)
      if (uploadError) { alert('Erreur upload : ' + uploadError.message); continue }
      const { data: urlData } = supabase.storage.from('chantier-photos').getPublicUrl(path)
      const { error: insertError } = await supabase.from('photos').insert([{
        url: urlData.publicUrl,
        chantier_id: tache ? tache.chantier_id : null,
        tache_id: tache ? tache.id : null,
        description: description || file.name,
      }])
      if (insertError) { alert('Erreur sauvegarde : ' + insertError.message); }
    }
    setLoading(false)
    onUploaded()
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Ajouter des photos</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {tache && (
              <div style={{ padding: '10px 12px', background: 'var(--primary-light)', borderRadius: 'var(--radius-sm)', marginBottom: 16, fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>
                Tache : {tache.titre}
              </div>
            )}
            <div className="form-group">
              <label>Photos *</label>
              <div className="upload-zone" onClick={() => document.getElementById('photo-input').click()}>
                {files.length === 0 ? (
                  <div>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                    <div>Cliquez pour selectionner</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>JPG, PNG, HEIC</div>
                  </div>
                ) : (
                  <div style={{ color: 'var(--primary)', fontWeight: 500 }}>
                    {files.length} photo{files.length > 1 ? 's' : ''} selectionnee{files.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <input id="photo-input" type="file" hidden multiple accept="image/*"
                onChange={e => setFiles(Array.from(e.target.files))} />
            </div>
            <div className="form-group">
              <label>Description (optionnel)</label>
              <input className="form-input" value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Avancement carrelage..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-green" disabled={loading || files.length === 0}>
              {loading ? 'Envoi...' : 'Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Vue Taches ───────────────────────────────────────────────────────────────

function VueTaches({ taches, chantiers, loading, onRefresh }) {
  const [filter, setFilter] = useState('actives')
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [photoTache, setPhotoTache] = useState(null)

  const chantiersMap = Object.fromEntries(chantiers.map(c => [c.id, c]))

  const filtered = filter === 'tous' ? taches
    : filter === 'actives' ? taches.filter(t => t.statut === 'en_attente' || t.statut === 'en_cours')
    : taches.filter(t => t.statut === filter)

  const updateStatut = async (id, statut) => {
    await supabase.from('taches').update({ statut }).eq('id', id)
    onRefresh()
  }

  if (loading) return <div className="loading-state">Chargement des taches...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Mes taches ({filtered.length})</h2>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'actives', label: 'Actives' },
          { key: 'tous', label: 'Toutes' },
          { key: 'en_attente', label: 'En attente' },
          { key: 'en_cours', label: 'En cours' },
          { key: 'termine', label: 'Terminees' },
          { key: 'valide', label: 'Validees' },
          { key: 'reserve', label: '⚠️ Reserves' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className="btn btn-outline btn-sm"
            style={filter === f.key ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' } : {}}>
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          <div className="empty-state-text">Aucune tache dans cette categorie.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(t => (
            <div key={t.id} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{t.titre}</span>
                    <span className={'badge ' + (TACHE_STATUT_BADGE[t.statut] || 'badge-gray')}>
                      {TACHE_STATUT_LABELS[t.statut] || t.statut}
                    </span>
                    {t.priorite === 'urgente' && <span className="badge badge-red">Urgent</span>}
                    {t.priorite === 'haute' && <span className="badge badge-orange">Haute priorite</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--gray-400)', flexWrap: 'wrap' }}>
                    {chantiersMap[t.chantier_id] && <span>🏗️ {chantiersMap[t.chantier_id].nom}</span>}
                    {t.date_echeance && <span>📅 {new Date(t.date_echeance).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  {t.description && (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6 }}>
                      {t.description}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {t.statut === 'en_attente' && (
                    <button className="btn btn-green btn-sm" onClick={() => updateStatut(t.id, 'en_cours')}>
                      Demarrer
                    </button>
                  )}
                  {t.statut === 'en_cours' && (
                    <button className="btn btn-green btn-sm" onClick={() => updateStatut(t.id, 'termine')}>
                      Terminer
                    </button>
                  )}
                  {t.statut === 'reserve' && (
                    <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                      ⚠️ Reserves a lever
                    </div>
                  )}
                  {(t.statut === 'en_cours' || t.statut === 'termine' || t.statut === 'reserve') && (
                    <button className="btn btn-outline btn-sm" onClick={() => { setPhotoTache(t); setShowPhotoModal(true) }}>
                      📷 Photos
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showPhotoModal && (
        <ModalUploadPhoto
          tache={photoTache}
          onClose={() => { setShowPhotoModal(false); setPhotoTache(null) }}
          onUploaded={onRefresh}
        />
      )}
    </div>
  )
}

// ─── Vue Photos ───────────────────────────────────────────────────────────────

function VuePhotos() {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [preview, setPreview] = useState(null)
  const [showUpload, setShowUpload] = useState(false)

  const loadPhotos = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('photos').select('*').order('created_at', { ascending: false })
    setPhotos(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { loadPhotos() }, [loadPhotos])

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Photos de chantier</h2>
        <button className="btn btn-green" onClick={() => setShowUpload(true)}>+ Ajouter</button>
      </div>
      {loading ? <div className="loading-state">Chargement...</div> : photos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📷</div>
          <div className="empty-state-text">Aucune photo. Documentez l avancement !</div>
        </div>
      ) : (
        <div className="photo-grid">
          {photos.map(p => (
            <div key={p.id} className="photo-item" onClick={() => setPreview(p)}>
              <img src={p.url} alt={p.description || 'Photo'} loading="lazy" />
              {p.description && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', padding: '6px 8px', fontSize: 11 }}>
                  {p.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div style={{ maxWidth: 800, width: '100%', background: 'white', borderRadius: 14, overflow: 'hidden' }}>
            <img src={preview.url} alt={preview.description} style={{ width: '100%', display: 'block' }} />
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setPreview(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
      {showUpload && <ModalUploadPhoto tache={null} onClose={() => setShowUpload(false)} onUploaded={loadPhotos} />}
    </div>
  )
}

// ─── Vue Chantiers (lecture) ──────────────────────────────────────────────────

function VueChantiers({ chantiers, loading }) {
  if (loading) return <div className="loading-state">Chargement...</div>
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Mes chantiers ({chantiers.length})</h2>
      </div>
      {chantiers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏗️</div>
          <div className="empty-state-text">Aucun chantier rejoint. Entrez un code depuis le tableau de bord.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {chantiers.map(c => (
            <div key={c.id} className="chantier-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div className="chantier-card-title">{c.nom}</div>
                <span className={'badge ' + (c.statut === 'en_cours' ? 'badge-blue' : c.statut === 'termine' ? 'badge-green' : 'badge-gray')}>
                  {c.statut === 'en_cours' ? 'En cours' : c.statut === 'termine' ? 'Termine' : c.statut || 'Planifie'}
                </span>
              </div>
              {c.adresse && <div className="chantier-card-meta"><span>📍 {c.adresse}</span></div>}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-500)', marginBottom: 6 }}>
                  <span>Avancement</span>
                  <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{c.progression || 0}%</span>
                </div>
                <div className="progress-bar-wrap">
                  <div className="progress-bar-fill" style={{ width: (c.progression || 0) + '%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Vue Documents ────────────────────────────────────────────────────────────

function VueDocuments({ chantiers }) {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const loadDocs = useCallback(async () => {
    const { data } = await supabase.from('documents').select('*').order('created_at', { ascending: false })
    setDocuments(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { loadDocs() }, [loadDocs])

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) {
      alert('Fichier trop volumineux. La taille maximale autorisée est 50 Mo.')
      e.target.value = ''
      return
    }
    setUploading(true)
    const path = 'documents/' + Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const { error: uploadError } = await supabase.storage.from('chantier-docs').upload(path, file)
    if (uploadError) { alert('Erreur upload : ' + uploadError.message); setUploading(false); return }
    const { data: urlData } = supabase.storage.from('chantier-docs').getPublicUrl(path)
    const { error: insertError } = await supabase.from('documents').insert([{
      nom: file.name, url: urlData.publicUrl, type: file.type,
      taille: file.size, chantier_id: null,
    }])
    if (insertError) { alert('Erreur sauvegarde : ' + insertError.message) }
    setUploading(false)
    loadDocs()
  }

  if (loading) return <div className="loading-state">Chargement...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Documents</h2>
        <label className="btn btn-green btn-sm" style={{ cursor: 'pointer' }}>
          {uploading ? 'Envoi...' : '+ Ajouter un document'}
          <input type="file" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {documents.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">📄</div>
          <div className="empty-state-text">Aucun document disponible.</div></div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Nom</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id}>
                  <td style={{ fontWeight: 500 }}>{doc.nom}</td>
                  <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{doc.created_at ? new Date(doc.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td><a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Telecharger</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Dashboard Overview Entreprise ───────────────────────────────────────────

function DashboardOverview({ taches, chantiers, userId, onNavigate, onRejoint }) {
  const enCours = taches.filter(t => t.statut === 'en_cours').length
  const enAttente = taches.filter(t => t.statut === 'en_attente').length
  const urgentes = taches.filter(t => (t.priorite === 'urgente' || t.priorite === 'haute') && (t.statut === 'en_attente' || t.statut === 'en_cours'))

  return (
    <div>
      <div id="step-rejoindre"><RejoindreChantier userId={userId} onRejoint={onRejoint} /></div>

      <div id="step-chantiers-liste" className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value">{taches.length}</div><div className="stat-label">Taches assignees</div></div>
            <div className="stat-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>📋</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value" style={{ color: 'var(--info)' }}>{enCours}</div><div className="stat-label">En cours</div></div>
            <div className="stat-icon" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>🔄</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value" style={{ color: '#c05621' }}>{enAttente}</div><div className="stat-label">En attente</div></div>
            <div className="stat-icon" style={{ background: 'var(--warning-bg)', color: '#c05621' }}>⏳</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value">{chantiers.length}</div><div className="stat-label">Chantiers</div></div>
            <div className="stat-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>🏗️</div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Taches actives</span>
            <button className="btn btn-outline btn-sm" onClick={() => onNavigate('taches')}>Voir tout</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {enCours + enAttente === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}>
                <div className="empty-state-text">Aucune tache active</div>
              </div>
            ) : (
              taches.filter(t => t.statut === 'en_attente' || t.statut === 'en_cours').slice(0, 5).map(t => (
                <div key={t.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{t.titre}</div>
                    {t.date_echeance && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>📅 {new Date(t.date_echeance).toLocaleDateString('fr-FR')}</div>}
                  </div>
                  <span className={'badge ' + (TACHE_STATUT_BADGE[t.statut] || 'badge-gray')}>{TACHE_STATUT_LABELS[t.statut]}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Priorites hautes</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            {urgentes.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}>
                <div className="empty-state-text">Aucune tache urgente</div>
              </div>
            ) : (
              urgentes.map(t => (
                <div key={t.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{t.titre}</span>
                  <span className={'badge ' + (t.priorite === 'urgente' ? 'badge-red' : 'badge-orange')}>
                    {t.priorite === 'urgente' ? 'Urgent' : 'Haute'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Vue Messages ─────────────────────────────────────────────────────────────

function VueMessages({ chantiers, user }) {
  const [selectedId, setSelectedId] = React.useState(null)
  const nomEntreprise = user?.email || 'Entreprise'

  React.useEffect(() => {
    if (chantiers.length > 0 && !selectedId) setSelectedId(chantiers[0].id)
  }, [chantiers, selectedId])

  const chantier = chantiers.find(c => c.id === selectedId)

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 160px)', minHeight: 500 }}>
      <div style={{ width: 240, background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14, color: '#2d3748' }}>Chantiers</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {chantiers.length === 0
            ? <div style={{ padding: 20, color: '#a0aec0', fontSize: 13 }}>Rejoignez un chantier pour acceder aux messages.</div>
            : chantiers.map(c => (
              <button key={c.id} onClick={() => setSelectedId(c.id)} style={{
                display: 'block', width: '100%', padding: '12px 18px', border: 'none', cursor: 'pointer',
                textAlign: 'left', background: selectedId === c.id ? '#e8f7f2' : 'white',
                borderLeft: selectedId === c.id ? '3px solid #1D9E75' : '3px solid transparent',
                borderBottom: '1px solid #f5f6fa',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: selectedId === c.id ? '#1D9E75' : '#2d3748' }}>{c.nom}</div>
              </button>
            ))}
        </div>
      </div>
      {chantier ? (
        <div style={{ flex: 1, background: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: 15, color: '#2d3748', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            💬 {chantier.nom}
          </div>
          <div style={{ fontSize: 11, color: '#92400e', padding: '5px 20px', background: '#fffbeb', borderBottom: '1px solid #fde68a', flexShrink: 0 }}>
            Discussion interne — visible par l'architecte et les entreprises du chantier
          </div>
          <ChantierChat chantierId={chantier.id} canal="interne" currentUserId={user?.id} currentUserNom={nomEntreprise} />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', borderRadius: 12, color: '#a0aec0', fontSize: 14 }}>
          Selectionnez un chantier
        </div>
      )}
    </div>
  )
}

// ─── Vue Notifications ────────────────────────────────────────────────────────

function VueNotifications({ userId }) {
  const [notifs, setNotifs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    supabase.from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setNotifs(data || [])
        setLoading(false)
        // Marquer toutes comme lues
        supabase.from('notifications').update({ lu: true }).eq('user_id', userId).eq('lu', false)
      })
  }, [userId])

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#718096' }}>Chargement...</div>

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>Notifications</h2>
        <span style={{ fontSize: 13, color: '#718096' }}>{notifs.length} notification{notifs.length !== 1 ? 's' : ''}</span>
      </div>
      {notifs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px', color: '#718096' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucune notification</div>
          <div style={{ fontSize: 13 }}>Vous recevrez ici les mises à jour de vos chantiers.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notifs.map(n => (
            <div key={n.id} className="card" style={{
              padding: '14px 18px',
              borderLeft: n.lu ? '3px solid #e2e8f0' : '3px solid #1D9E75',
              opacity: n.lu ? 0.75 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1a202c', marginBottom: 4 }}>
                    {!n.lu && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', marginRight: 8, verticalAlign: 'middle' }} />}
                    {n.titre}
                  </div>
                  {n.contenu && <div style={{ fontSize: 13, color: '#4a5568', lineHeight: 1.5 }}>{n.contenu}</div>}
                </div>
                <div style={{ fontSize: 11, color: '#a0aec0', whiteSpace: 'nowrap', marginTop: 2 }}>
                  {new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Composant Principal Entreprise ──────────────────────────────────────────

export default function EntrepriseDashboard({ activePage, onNavigate, user, onRelancerTutoriel }) {
  const [taches, setTaches] = useState([])
  const [chantiers, setChantiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    // Charger les chantiers via chantier_membres
    const { data: membresData, error: membresError } = await supabase
      .from('chantier_membres')
      .select('chantier_id')
      .eq('user_id', user.id)

    let chantiersData = []
    if (!membresError && membresData && membresData.length > 0) {
      const ids = membresData.map(m => m.chantier_id)
      const { data } = await supabase.from('chantiers').select('*').in('id', ids)
      chantiersData = data || []
    }
    setChantiers(chantiersData)

    // Charger les taches assignees a l'entreprise ou non assignees
    if (chantiersData.length > 0) {
      const ids = chantiersData.map(c => c.id)
      const { data: tachesData } = await supabase
        .from('taches')
        .select('*')
        .in('chantier_id', ids)
        .or('assigned_to.eq.' + user.id + ',assigned_to.is.null')
        .order('created_at', { ascending: false })
      setTaches(tachesData || [])
    } else {
      setTaches([])
    }
    setLoading(false)
  }, [user.id])

  useEffect(() => { loadData() }, [loadData])

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <DashboardOverview taches={taches} chantiers={chantiers} userId={user.id} onNavigate={onNavigate} onRejoint={loadData} />
      case 'taches': return <VueTaches taches={taches} chantiers={chantiers} loading={loading} onRefresh={loadData} />
      case 'photos': return <VuePhotos />
      case 'chantiers': return <VueChantiers chantiers={chantiers} loading={loading} />
      case 'documents': return <VueDocuments chantiers={chantiers} />
      case 'notifications': return <VueNotifications userId={user.id} />
      case 'messages': return <VueMessages chantiers={chantiers} user={user} />
      default: return <DashboardOverview taches={taches} chantiers={chantiers} userId={user.id} onNavigate={onNavigate} onRejoint={loadData} />
    }
  }

  return (
    <div>
      {renderPage()}
      {showOnboarding && (
        <OnboardingTour role="entreprise" onTermine={() => setShowOnboarding(false)} />
      )}
    </div>
  )
}
